import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react'
import { AnimatePresence } from 'motion/react'
import { Fab, type SymbolName } from '../ui'

export interface FabWunsch {
  label: string
  icon: SymbolName
  onClick: () => void
}

const Kontext = createContext<((wunsch: FabWunsch | null) => void) | null>(null)

/**
 * Die Haupthandlung gehört der SEITE, angezeigt wird sie von der SCHALE.
 *
 * Jede Seite meldet ihre eine Handlung an; die Schale rendert daraus den
 * Knopf an der Stelle, die zur Fensterbreite passt. Ohne diesen Umweg müsste
 * jede Seite ihren eigenen schwebenden Knopf positionieren — und jede würde
 * es ein bisschen anders tun.
 */
export function FabProvider({ children }: { children: ReactNode }) {
  const [wunsch, setWunsch] = useState<FabWunsch | null>(null)
  return (
    <Kontext.Provider value={setWunsch}>
      {children}
      <div className="fab-platz">
        <AnimatePresence>
          {wunsch ? (
            <Fab
              key={wunsch.label}
              icon={wunsch.icon}
              label={wunsch.label}
              onClick={wunsch.onClick}
            />
          ) : null}
        </AnimatePresence>
      </div>
    </Kontext.Provider>
  )
}

/**
 * Meldet die Haupthandlung dieser Seite an. `null` heißt: diese Seite hat
 * keine.
 *
 * ⚠️ Neu angemeldet wird nur, wenn sich Beschriftung oder Zeichen ändern.
 * Die Rückruffunktion ist bei jedem Rendern eine neue — hinge die Anmeldung
 * daran, liefe sie in einer Schleife. Sie wird deshalb über eine Referenz
 * frisch gehalten.
 */
export function useFab(wunsch: FabWunsch | null): void {
  const setWunsch = useContext(Kontext)
  const aktuell = useRef(wunsch)
  aktuell.current = wunsch
  const label = wunsch?.label
  const icon = wunsch?.icon

  useEffect(() => {
    if (!setWunsch) return
    if (!label || !icon) {
      setWunsch(null)
      return
    }
    setWunsch({
      label,
      icon,
      onClick: () => {
        aktuell.current?.onClick()
      },
    })
    return () => {
      setWunsch(null)
    }
  }, [setWunsch, label, icon])
}
